import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { CreateOrderActions, OnApproveActions, OnApproveData } from "@paypal/paypal-js";
import useBrand from 'Hooks/useBrand';
import { CurrencyCodesType } from "modules/Types";
import { clsx } from 'modules/Utils';
import { useState } from 'react';
import PayPalCardFieldsWithAddressAutocomplete from "components/PayPal/PayPalCardFieldsWithAddressAutocomplete";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";

const payPalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID as string;

interface PayPalButtonGroupProps {
    /**
     * Callback function to create a PayPal order
     */
    createOrder?: (data: Record<string, unknown>, actions: CreateOrderActions) => Promise<string>;
    /**
     * Callback function when PayPal order is cancelled
     */
    onCancel?: () => void;
    /**
     * Callback function when PayPal order encounters an error
     */
    onError?: () => void;
    /**
     * Callback function when PayPal order is approved
     */
    onApprove?: (data: OnApproveData, actions: OnApproveActions) => Promise<void>;
    /**
     * Additional class names to apply to the container
     */
    className?: string;
    /**
     * The currency code of the brand
     */
    currencyCode?: CurrencyCodesType;
    /**
     * The style of the PayPal buttons
     */
    style?: React.CSSProperties;

    /**
     * Whether to allow the checkout without being a Pro brand
     */
    allowWithoutPro?: boolean;
    /**
     * Whether the PayPal buttons should be disabled
     */
    disabled?: boolean;
}

/**
 * A component that renders PayPal and Venmo payment buttons
 * Checks for eligibility based on Pro status and currency
 */
enum PaymentMethod {
    CARD = 'card',
    PAYPAL = 'paypal'
}

const PayPalButtonGroup = ({
    createOrder,
    onCancel,
    onError,
    onApprove,
    className,
    currencyCode,
    style,
    allowWithoutPro = false,
    disabled = false
}: PayPalButtonGroupProps) => {

    const { isProBrand, currentBrand } = useBrand();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>(PaymentMethod.PAYPAL);

    // Check if user can use PayPal based on Pro status and allowWithoutPro override
    const canUsePayPal = isProBrand || allowWithoutPro;
    
    // Determine country bias for address autocomplete based on brand currency
    const getBiasCountry = () => {
        const currencyToCountryMap: Record<string, string> = {
            'USD': 'US',
            'CAD': 'CA', 
            'GBP': 'GB',
            'EUR': 'DE', // Default to Germany for EUR
            'AUD': 'AU',
            'JPY': 'JP',
            'CHF': 'CH',
            'SEK': 'SE',
            'NOK': 'NO',
            'DKK': 'DK'
        };
        return currencyToCountryMap[currencyCode || 'USD'] || 'US';
    };
    
    // PayPal supports international payments with currency conversion to USD
    // All currencies are supported through automatic conversion
    const isUsdCurrency = currencyCode === 'USD';
    const supportsPayPal = true; // PayPal now supports all currencies via conversion

    /**
     * Renders payment method selection tabs
     */
    const renderPaymentMethodSelector = () => (
        <div style={{ marginBottom: 16 }}>
            <div style={{ 
                display: 'flex', 
                gap: 16,
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: 8
            }}>
                {/* <TabsSymphony
                    className="w-full"
                    selectedTab={selectedPaymentMethod}
                    onChange={(tab: string | TabItem) => setSelectedPaymentMethod(tab as PaymentMethod)}
                    tabs={[{
                        title: 'Credit/Debit Card',
                        id: PaymentMethod.CARD
                    }, {
                        title: 'PayPal Account',
                        id: PaymentMethod.PAYPAL
                    }]}
                /> */}
        
                {/* <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod(PaymentMethod.CARD)}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: '8px 0',
                        fontSize: 14,
                        fontWeight: selectedPaymentMethod === PaymentMethod.CARD ? 600 : 400,
                        color: selectedPaymentMethod === PaymentMethod.CARD ? '#1f2937' : '#6b7280',
                        borderBottom: selectedPaymentMethod === PaymentMethod.CARD ? '2px solid #3b82f6' : 'none',
                        cursor: 'pointer'
                    }}
                >
                    💳 Credit/Debit Card
                </button>
                <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod(PaymentMethod.PAYPAL)}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: '8px 0',
                        fontSize: 14,
                        fontWeight: selectedPaymentMethod === PaymentMethod.PAYPAL ? 600 : 400,
                        color: selectedPaymentMethod === PaymentMethod.PAYPAL ? '#1f2937' : '#6b7280',
                        borderBottom: selectedPaymentMethod === PaymentMethod.PAYPAL ? '2px solid #3b82f6' : 'none',
                        cursor: 'pointer'
                    }}
                >
                    🅿️ PayPal Account
                </button> */}
            </div>
        </div>
    );

    /**
     * Renders active PayPal payment methods with card fields and buttons
     */
    const renderActivePayPalPaymentMethods = () => {
        return (
            <PayPalScriptProvider options={{ 
                clientId: payPalClientId,
                currency: isUsdCurrency ? currencyCode : 'USD', // PayPal processes in USD for international
                components: 'buttons,card-fields', // Enable both buttons and card fields
                'enable-funding': 'venmo',
                'data-page-type': 'product-details'
            }}>
                <div>
                    {/* Payment Method Selection */}
                    {renderPaymentMethodSelector()}
                    
                    {/* Selected Payment Method */}
                    {selectedPaymentMethod === PaymentMethod.CARD ? (
                        <PayPalCardFieldsWithAddressAutocomplete
                            createOrder={createOrder}
                            onCancel={onCancel}
                            onError={onError}
                            googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                            biasCountry={getBiasCountry()}
                            onApprove={onApprove}
                            className="w-full"
                            disabled={disabled}
                        />
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <PayPalButtons
                                className="w-full relative z-0"
                                style={{ label: "pay", shape: "pill" }}
                                fundingSource="paypal"
                                createOrder={createOrder}
                                onCancel={onCancel}
                                onError={onError}
                                onApprove={onApprove}
                            />
                            <PayPalButtons
                                className="w-full relative z-0"
                                style={{ label: "pay", shape: "pill" }}
                                fundingSource="venmo"
                                createOrder={createOrder}
                                onCancel={onCancel}
                                onError={onError}
                                onApprove={onApprove}
                            />
                        </div>
                    )}
                </div>
            </PayPalScriptProvider>
        );
    };

    /**
     * Renders the appropriate PayPal button based on eligibility conditions
     * @returns JSX.Element The appropriate PayPal button component
     */
    const renderPayPalButton = () => {
        // PayPal now supports all currencies through automatic USD conversion
        // All eligible users can use PayPal regardless of currency
        return renderActivePayPalPaymentMethods();
    };

    return (
        <div className={clsx(className, "payPalButtonContainer")} style={style}>
            {renderPayPalButton()}
        </div>
    );
};

export default PayPalButtonGroup;